import { scanClientApiV1 as scanClientApi } from '@deepup/apis';
import { request } from '@core/api';
import { errorBox, infoBox } from '@core/components/AlertBox';
import { dateTimeFormatter } from '@core/logic';
import { useAppDispatch } from '@core/redux/hooks';
import { getTransport } from '@core/redux/transport';
import { getScanManualPositionValue } from '@scans/helper';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { ProcessingResultsList } from '@processingResults/components';
import { processingResultSelectors } from '@processingResults/redux';
import type { ProcessingResult } from '@processingResults/types';
import { scanActions, scanThunks } from '@scans/redux';
import type { Scan } from '@scans/types';
import { viewer3dSelectors } from '@viewer3D/redux';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { elementActions, elementSelectors, elementThunks } from '@elements/redux';
import {
  pointElementActions,
  pointElementSelectors,
  pointElementThunks,
} from '@elements/redux/point-element.slice';
import {
  markerSphereActions,
  markerSphereSelectors,
  markerSphereThunks,
} from '@elements/redux/markers.slice';
import { trenchActions, trenchSelectors, trenchThunks } from '@trenches/redux';
import {
  toggleScanPrelabelVisibility,
  useScanPrelabelState,
  ingestScanPrelabel,
} from '../ScanPrelabel';
import { ScanCategoryDetails } from './ScanCategoryDetails';
import { ScanTransformation } from './ScanTransformation';
import { SurfaceDetails } from './SurfaceDetails';

const getScanDuration = (beginDate: string, endDate: string): number => {
  return Math.round((new Date(endDate).getTime() - new Date(beginDate).getTime()) / 1000);
};

const buildScanProperties = (scan: Scan): [string, string][] => {
  const { metadata, location, id, deviceName, uploadedAt, updatedAt } = scan;

  const manualPositionValue = getScanManualPositionValue(scan);

  return [
    ['ID', id],
    deviceName ? ['Device Name', deviceName] : undefined,
    location?.place ? ['Location', location.place] : undefined,
    ['Recorded At', metadata.beginDate ? dateTimeFormatter(metadata.beginDate) : 'unknown'],
    metadata.beginDate && metadata.endDate
      ? ['Duration', getScanDuration(metadata.beginDate, metadata.endDate).toString()]
      : undefined,
    manualPositionValue ? ['Manual Position', manualPositionValue] : undefined,
    ['Uploaded At', dateTimeFormatter(uploadedAt)],
    ['Last Updated', dateTimeFormatter(updatedAt)],
  ].filter(Boolean) as [string, string][];
};

const EXCLUSION_TYPES = [
  { type: 'fitting', label: 'Fittings' },
  { type: 'duct', label: 'Ducts' },
  { type: 'trench', label: 'Trenches' },
  { type: 'marker', label: 'Markers' },
] as const;

export const ScanDetails = () => {
  const dispatch = useAppDispatch();
  const scan = useSelector(viewer3dSelectors.selectedScan);
  const processingResults = useSelector(
    processingResultSelectors.selectByProps({ scanId: scan?.id }),
  );

  const selectedProcessingResult = processingResults?.find(
    (pr) => pr.id === scan?.selectedProcessingResultId,
  );

  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isApplyingPrelabel, setIsApplyingPrelabel] = useState(false);
  const [excludedTypes, setExcludedTypes] = useState<string[]>([]);
  const { show: showScanPrelabel } = useScanPrelabelState();

  const currentTrenches = useSelector(trenchSelectors.selectAll);
  const currentElements = useSelector(elementSelectors.selectAll);
  const currentPointElements = useSelector(pointElementSelectors.selectAll);
  const currentMarkerSpheres = useSelector(markerSphereSelectors.selectAll);

  const handleToggleExcludedType = useCallback((type: string) => {
    setExcludedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  }, []);

  const handleApplyPrelabel = useCallback(async () => {
    if (!scan?.id) return;
    setIsApplyingPrelabel(true);
    try {
      await ingestScanPrelabel(scan.id, excludedTypes);
      infoBox('Prelabel applied successfully as real data');

      if (scan.projectId) {
        const results = await Promise.allSettled([
          dispatch(
            trenchThunks.getMany({ path: ['projects', scan.projectId, 'trenches'] }),
          ).unwrap(),
          dispatch(
            elementThunks.getMany({ path: ['projects', scan.projectId, 'splineElements'] }),
          ).unwrap(),
          dispatch(
            pointElementThunks.getMany({ path: ['projects', scan.projectId, 'pointElements'] }),
          ).unwrap(),
          dispatch(
            markerSphereThunks.getMany({ path: ['projects', scan.projectId, 'markers'] }),
          ).unwrap(),
        ]);

        const [trenchResult, elementResult, pointElementResult, markerSphereResult] = results;

        if (trenchResult.status === 'fulfilled') {
          const newIds = new Set(trenchResult.value.map((t) => t.id));
          const staleIds = currentTrenches.map((t) => t.id).filter((id) => !newIds.has(id));
          if (staleIds.length > 0) {
            dispatch(trenchActions.removeMany(staleIds));
          }
        } else {
          console.error('Failed to refetch trenches:', trenchResult.reason);
          errorBox('Failed to refetch trenches');
        }

        if (elementResult.status === 'fulfilled') {
          const newIds = new Set(elementResult.value.map((e) => e.id));
          const staleIds = currentElements.map((e) => e.id).filter((id) => !newIds.has(id));
          if (staleIds.length > 0) {
            dispatch(elementActions.removeMany(staleIds));
          }
        } else {
          console.error('Failed to refetch spline elements:', elementResult.reason);
          errorBox('Failed to refetch spline elements');
        }

        if (pointElementResult.status === 'fulfilled') {
          const newIds = new Set(pointElementResult.value.map((pe) => pe.id));
          const staleIds = currentPointElements.map((pe) => pe.id).filter((id) => !newIds.has(id));
          if (staleIds.length > 0) {
            dispatch(pointElementActions.removeMany(staleIds));
          }
        } else {
          console.error('Failed to refetch point elements:', pointElementResult.reason);
          errorBox('Failed to refetch point elements');
        }

        if (markerSphereResult.status === 'fulfilled') {
          const newIds = new Set(markerSphereResult.value.map((m) => m.id));
          const staleIds = currentMarkerSpheres.map((m) => m.id).filter((id) => !newIds.has(id));
          if (staleIds.length > 0) {
            dispatch(markerSphereActions.removeMany(staleIds));
          }
        } else {
          console.error('Failed to refetch marker spheres:', markerSphereResult.reason);
          errorBox('Failed to refetch marker spheres');
        }
      }

      if (showScanPrelabel) {
        toggleScanPrelabelVisibility();
      }
    } catch (error) {
      console.error('Error applying prelabel as real data:', error);
      errorBox('Failed to apply prelabel as real data');
    } finally {
      setIsApplyingPrelabel(false);
    }
  }, [
    scan?.id,
    scan?.projectId,
    excludedTypes,
    showScanPrelabel,
    dispatch,
    currentTrenches,
    currentElements,
    currentPointElements,
    currentMarkerSpheres,
  ]);

  const selectResultHandler = useCallback(
    async (result: ProcessingResult) => {
      if (!scan?.id) return;
      const previousId = scan.selectedProcessingResultId;
      if (previousId === result.id) return;

      dispatch(
        scanActions.updateOne({
          id: scan.id,
          selectedProcessingResultId: result.id,
        }),
      );

      // Bypass scanThunks.patch on purpose: its fulfilled action triggers the
      // factory's upsertMany reducer, which would shallow-merge the response
      // body over the entity and clobber the optimistic update if the response
      // carries any value for selectedProcessingResultId. The optimistic
      // dispatch above is authoritative for this field.
      try {
        await request('PATCH', {
          path: ['scans', scan.id],
          body: {
            id: scan.id,
            selectedProcessingResultId: result.id,
          },
        });
      } catch (error) {
        dispatch(
          scanActions.updateOne({
            id: scan.id,
            selectedProcessingResultId: previousId,
          }),
        );
        errorBox('Failed to update processing result');
        console.error('Error updating processing result:', error);
      }
    },
    [scan?.id, scan?.selectedProcessingResultId, dispatch],
  );

  const copyToClipboard = useCallback((key: string, text: string) => {
    navigator.clipboard.writeText(text);
    infoBox(`Copied ${key} to clipboard`);
  }, []);

  useEffect(() => {
    if (!scan?.id || scan.detailsFetched || isLoadingDetails) return;

    const fetchDetails = async () => {
      setIsLoadingDetails(true);
      try {
        const scandPromise = (async () => {
          try {
            const transport = getTransport();
            const client = new scanClientApi.ScanServiceClient(transport);
            return await client.getScan({ id: scan.id, version: BigInt(1) }).response;
          } catch (err) {
            console.warn('Failed to fetch scan from Scand:', err);
            return null;
          }
        })();

        const [_, scandScan] = await Promise.all([
          dispatch(
            scanThunks.getOne({
              path: ['scans', scan.id],
              params: { projection: 'scanDetail' },
            }),
          ).unwrap(),
          scandPromise,
        ]);

        dispatch(
          scanActions.updateOne({
            id: scan.id,
            detailsFetched: true,
            ...(scandScan && {
              manualPositionCorrectionConfiguration:
                scandScan.manualPositionCorrectionConfiguration,
              manualPositionCorrectionReferencePoints:
                scandScan.manualPositionCorrectionReferencePoints,
            }),
          }),
        );
      } catch (error) {
        errorBox('Failed to load scan details');
        console.error('Error fetching scan details:', error);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchDetails();
  }, [dispatch, scan?.id, scan?.detailsFetched, isLoadingDetails]);

  if (!scan) return null;

  const { surfaceClassification, id } = scan;
  const scanProps = buildScanProperties(scan);

  return (
    <>
      <List dense>
        {scanProps.map(([primary, secondary]) => (
          <ListItemButton key={primary} onClick={() => copyToClipboard(primary, secondary)}>
            <ListItemText primary={primary} secondary={secondary} />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <Stack gap={1} padding={2}>
        <Typography variant="overline">Scan Category</Typography>
        {scan.projectId && <ScanCategoryDetails scanId={id} projectId={scan.projectId} />}
        {!scan.projectId && (
          <Typography variant="body2" color="text.secondary">
            No project assigned
          </Typography>
        )}
      </Stack>
      <Divider />
      <Stack gap={1} padding={2}>
        <Typography variant="overline">Scan Surface Type</Typography>
        <SurfaceDetails surfaceClassification={surfaceClassification} scanId={id} />
      </Stack>
      <Divider />
      <Stack gap={1} padding={2}>
        <Typography variant="overline">Prelabel Preview</Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={showScanPrelabel}
              onChange={toggleScanPrelabelVisibility}
              size="small"
            />
          }
          label="Show prelabel on 3D mesh"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
          Exclude from ingestion
        </Typography>
        <FormGroup row>
          {EXCLUSION_TYPES.map(({ type, label }) => (
            <FormControlLabel
              key={type}
              control={
                <Checkbox
                  checked={excludedTypes.includes(type)}
                  onChange={() => handleToggleExcludedType(type)}
                  disabled={!showScanPrelabel || isApplyingPrelabel}
                  size="small"
                />
              }
              label={label}
            />
          ))}
        </FormGroup>
        <Button
          size="small"
          variant="outlined"
          disabled={!showScanPrelabel || isApplyingPrelabel}
          onClick={handleApplyPrelabel}
          sx={{ alignSelf: 'flex-start', mt: 1 }}
        >
          {isApplyingPrelabel ? 'Applying...' : 'Apply as real data'}
        </Button>
      </Stack>
      <Divider />
      <ScanTransformation scan={scan} selectedProcessingResult={selectedProcessingResult} />
      <Divider />
      <Stack gap={1} padding={2}>
        <Typography variant="overline">Processing Results</Typography>
        <ProcessingResultsList
          selectedProcessingResultId={scan.selectedProcessingResultId}
          processingResults={processingResults}
          onSelectResult={selectResultHandler}
        />
      </Stack>
    </>
  );
};
